<template>
  <v-dialog 
    v-model="dialogVisible" 
    max-width="900"
    persistent
    class="split-modal-dialog"
  >
    <v-card class="split-modal-card">
      <!-- 헤더 -->
      <div class="split-modal-header">
        <div class="header-title">
          <v-icon size="22" color="white">mdi-call-split</v-icon>
          <span>통장내역 분개</span>
        </div>
        <button class="close-btn" @click="closeDialog">
          <v-icon size="20">mdi-close</v-icon>
        </button>
      </div>

      <!-- 원본 내역 정보 -->
      <div class="original-info">
        <div class="info-row">
          <span class="info-label">거래일</span>
          <span class="info-value">{{ formatDate(parentItem?.cb_date) }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">구분</span>
          <span class="info-value" :class="parentItem?.cb_gubun === 'IN' ? 'income' : 'expense'">
            {{ parentItem?.cb_gubun === 'IN' ? '수입' : '지출' }}
          </span>
        </div>
        <div class="info-row">
          <span class="info-label">금액</span>
          <span class="info-value amount">{{ formatNumber(parentItem?.cb_price) }}원</span>
        </div>
        <div class="info-row full">
          <span class="info-label">적요</span>
          <span class="info-value memo">{{ parentItem?.cb_memo || '-' }}</span>
        </div>
      </div>

      <!-- 분개 안내 -->
      <div class="split-guide">
        <v-icon size="16" color="#6366f1">mdi-information-outline</v-icon>
        <span>원본 금액을 여러 계정으로 나누어 설정할 수 있습니다. 분개 합계는 원본 금액과 일치해야 합니다.</span>
      </div>

      <!-- 분개 항목 리스트 -->
      <div class="split-items-container">
        <div class="split-items-header">
          <span class="header-label">분개 항목</span>
          <button class="add-item-btn" @click="addSplitItem">
            <v-icon size="16">mdi-plus</v-icon>
            항목 추가
          </button>
        </div>

        <div class="split-items-list">
          <div 
            v-for="(item, index) in splitItems" 
            :key="index"
            class="split-item"
          >
            <div class="item-num">{{ index + 1 }}</div>
            
            <div class="item-fields">
              <v-autocomplete
                v-model="item.scode"
                :items="accountCodeList"
                item-title="sname"
                item-value="scode"
                :custom-filter="accountFilter"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                auto-select-first
                placeholder="계정 검색"
                no-data-text="검색 결과 없음"
                class="field-autocomplete scode"
              />

              <select v-model="item.bankCode" class="field-select bank">
                <option value="" disabled>통장 선택</option>
                <option 
                  v-for="bank in bankList" 
                  :key="bank.mb_code" 
                  :value="bank.mb_code"
                >
                  {{ bank.mb_alias || bank.mb_bank_name }}
                </option>
              </select>

              <div class="field-price">
                <input
                  type="text"
                  :value="formatNumber(item.price)"
                  @input="handlePriceInput($event, index)"
                  placeholder="0"
                />
                <span>원</span>
              </div>

              <input
                type="text"
                v-model="item.memo"
                placeholder="적요"
                class="field-memo"
              />
            </div>

            <button 
              class="item-delete" 
              @click="removeSplitItem(index)"
              :disabled="splitItems.length <= 2"
            >
              <v-icon size="14">mdi-close</v-icon>
            </button>
          </div>
        </div>

        <!-- 합계 -->
        <div class="split-summary">
          <div class="summary-row">
            <span class="summary-label">분개 합계</span>
            <span class="summary-value" :class="{ error: !isSumValid }">
              {{ formatNumber(splitTotal) }}원
            </span>
          </div>
          <div class="summary-row">
            <span class="summary-label">원본 금액</span>
            <span class="summary-value original">{{ formatNumber(parentPrice) }}원</span>
          </div>
          <div class="summary-row difference" :class="{ valid: isSumValid, invalid: !isSumValid }">
            <span class="summary-label">차이</span>
            <span class="summary-value">
              {{ isSumValid ? '일치' : `${formatNumber(Math.abs(difference))}원 ${difference > 0 ? '초과' : '부족'}` }}
            </span>
          </div>
        </div>
      </div>

      <!-- 버튼 영역 -->
      <div class="split-modal-actions">
        <button
          v-if="hasSplits"
          class="btn-merge"
          type="button"
          :disabled="isSaving || isMerging"
          @click="mergeSplits"
        >
          <v-progress-circular v-if="isMerging" indeterminate size="16" width="2" color="#b91c1c" />
          <v-icon v-else size="16">mdi-call-merge</v-icon>
          분개 합치기
        </button>
        <button class="btn-cancel" type="button" :disabled="isSaving || isMerging" @click="closeDialog">
          취소
        </button>
        <button 
          class="btn-save" 
          type="button"
          @click="saveSplits"
          :disabled="!isSumValid || !isAllFieldsValid || isSaving || isMerging"
        >
          <v-progress-circular v-if="isSaving" indeterminate size="16" width="2" color="white" />
          <v-icon v-else size="16">mdi-content-save</v-icon>
          {{ hasSplits ? '수정' : '저장' }}
        </button>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue';
import { bankbookApi } from '@/services/api';

interface SplitItem {
  scode: string | null;
  price: number;
  memo: string;
  bankCode: string | null;
  sortOrder: number;
}

interface AccountCode {
  scode: string;
  sname: string;
  gubun: 'IN' | 'OUT';
  displayName: string;
}

interface BankItem {
  mb_code: string;
  mb_alias: string;
  mb_bank_name: string;
}

interface ParentItem {
  cb_idx: number;
  m_code: number;
  cb_biz_gubun: string | null;
  cb_gubun: 'IN' | 'OUT';
  cb_date: string;
  cb_price: number;
  cb_memo: string | null;
  cb_scode: string | null;
  cb_bank_code: string | null;
}

const props = defineProps<{
  modelValue: boolean;
  cbIdx: number | null;
  year?: string;
  accountCodes: AccountCode[];
  bankList: BankItem[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'saved'): void;
}>();

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
});

const parentItem = ref<ParentItem | null>(null);
const splitItems = ref<SplitItem[]>([]);
const hasSplits = ref(false);
const isSaving = ref(false);
const isMerging = ref(false);

const accountCodeList = computed(() => {
  if (!parentItem.value) return [];
  // 원본의 cb_gubun(IN/OUT)과 일치하는 계정만 표시
  const gubun = parentItem.value.cb_gubun;
  return props.accountCodes.filter(code => code.gubun === gubun);
});

/** 계정명·코드 모두 검색 */
const accountFilter = (
  _value: string,
  query: string,
  item?: { raw?: AccountCode }
) => {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const raw = item?.raw;
  const sname = String(raw?.sname || _value || '').toLowerCase();
  const scode = String(raw?.scode || '').toLowerCase();
  return sname.includes(q) || scode.includes(q);
};

const bankList = computed(() => props.bankList || []);

const parentPrice = computed(() => {
  return Number(parentItem.value?.cb_price) || 0;
});

const splitTotal = computed(() => {
  return splitItems.value.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
});

const difference = computed(() => {
  return splitTotal.value - parentPrice.value;
});

const isSumValid = computed(() => {
  return splitTotal.value === parentPrice.value;
});

const isAllFieldsValid = computed(() => {
  return splitItems.value.every(item => 
    item.scode && item.scode.trim() !== '' && 
    item.bankCode && item.bankCode.trim() !== '' &&
    Number(item.price) > 0
  );
});

const formatDate = (date: string | null | undefined) => {
  if (!date) return '-';
  return date.substring(0, 10);
};

const formatNumber = (num: number | string | null | undefined) => {
  if (num === null || num === undefined) return '0';
  const n = Number(num);
  if (isNaN(n)) return '0';
  return n.toLocaleString();
};

const handlePriceInput = (event: Event, index: number) => {
  const input = event.target as HTMLInputElement;
  const value = input.value.replace(/[^0-9]/g, '');
  splitItems.value[index].price = parseInt(value) || 0;
};

const addSplitItem = () => {
  splitItems.value.push({
    scode: null,
    price: 0,
    memo: '',
    bankCode: parentItem.value?.cb_bank_code || '',
    sortOrder: splitItems.value.length
  });
};

const removeSplitItem = (index: number) => {
  if (splitItems.value.length > 2) {
    splitItems.value.splice(index, 1);
    splitItems.value.forEach((item, idx) => {
      item.sortOrder = idx;
    });
  }
};

const loadSplits = async () => {
  if (!props.cbIdx) return;

  try {
    const response = await bankbookApi.getSplits(props.cbIdx, props.year);
    
    if (response.data.success) {
      const data = response.data.data;
      parentItem.value = data.parent;
      
      // 새로운 구조: 분개 항목이 있으면 hasSplits = true
      const splits = data.splits || [];
      hasSplits.value = splits.length > 0;

      if (splits.length > 0) {
        splitItems.value = splits.map((s: any, idx: number) => ({
          scode: s.cbs_scode,
          price: Number(s.cbs_price) || 0,
          memo: s.cbs_memo || '',
          bankCode: s.cbs_bank_code || '',
          sortOrder: idx
        }));
      } else {
        initEmptySplits();
      }
    }
  } catch (error: any) {
    console.error('분개 조회 오류:', error);
    alert('분개 정보를 불러오는데 실패했습니다.');
  }
};

const initEmptySplits = () => {
  const originalPrice = Number(parentItem.value?.cb_price) || 0;
  const halfPrice = Math.floor(originalPrice / 2);
  const defaultBankCode = parentItem.value?.cb_bank_code || '';
  
  splitItems.value = [
    {
      scode: null,
      price: halfPrice,
      memo: '',
      bankCode: defaultBankCode,
      sortOrder: 0
    },
    {
      scode: null,
      price: originalPrice - halfPrice,
      memo: '',
      bankCode: defaultBankCode,
      sortOrder: 1
    }
  ];
};

const saveSplits = async () => {
  if (!props.cbIdx || !isSumValid.value || !isAllFieldsValid.value) return;

  isSaving.value = true;
  try {
    const lines = splitItems.value.map(item => ({
      scode: item.scode || undefined,
      price: Number(item.price) || 0,
      memo: item.memo || undefined,
      bankCode: item.bankCode || undefined,
      sortOrder: item.sortOrder
    }));

    const response = await bankbookApi.saveSplits(props.cbIdx, lines);

    if (response.data.success) {
      alert(response.data.message || '분개가 저장되었습니다.');
      emit('saved');
      closeDialog();
    } else {
      alert(response.data.message || '분개 저장에 실패했습니다.');
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`분개 저장 실패: ${message}`);
    console.error('분개 저장 오류:', error);
  } finally {
    isSaving.value = false;
  }
};

const mergeSplits = async () => {
  if (!props.cbIdx || !hasSplits.value || isMerging.value) return;
  if (!confirm('분개된 항목을 합쳐 원본 한 건으로 되돌릴까요?')) return;

  isMerging.value = true;
  try {
    const response = await bankbookApi.clearSplits(props.cbIdx);
    if (response.data.success) {
      alert(response.data.message || '분개를 합쳤습니다.');
      emit('saved');
      closeDialog();
    } else {
      alert(response.data.message || '분개 합치기에 실패했습니다.');
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`분개 합치기 실패: ${message}`);
    console.error('분개 합치기 오류:', error);
  } finally {
    isMerging.value = false;
  }
};

const closeDialog = () => {
  dialogVisible.value = false;
};

watch(() => props.modelValue, (newVal) => {
  if (newVal && props.cbIdx) {
    loadSplits();
  }
});

watch(() => props.cbIdx, (newVal) => {
  if (newVal && props.modelValue) {
    loadSplits();
  }
});
</script>

<style lang="scss">
@import '@/assets/styles/variables';

.split-modal-dialog {
  .v-overlay__content {
    max-height: 90vh;
  }
}

.split-modal-card {
  border-radius: 10px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.split-modal-header {
  background: #1e3a5f;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    color: white;
    font-size: 14px;
    font-weight: 600;
  }

  .close-btn {
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 4px;
    padding: 4px;
    cursor: pointer;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

.original-info {
  background: #f8fafc;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  gap: 24px;
  border-bottom: 1px solid #e2e8f0;

  .info-row {
    display: flex;
    align-items: center;
    gap: 6px;

    &.full {
      flex: 1;
    }

    .info-label {
      font-size: 11px;
      color: #94a3b8;
    }

    .info-value {
      font-size: 13px;
      color: #1e293b;
      font-weight: 500;

      &.income { color: #16a34a; }
      &.expense { color: #dc2626; }
      &.amount { font-weight: 700; color: #1e3a5f; }
      &.memo { color: #64748b; font-weight: 400; }
    }
  }
}

.split-guide {
  padding: 8px 16px;
  background: #fef3c7;
  border-bottom: 1px solid #fcd34d;
  font-size: 11px;
  color: #92400e;
  display: flex;
  align-items: center;
  gap: 6px;
}

.split-items-container {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
  max-height: 350px;
}

.split-items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;

  .header-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
  }

  .add-item-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 10px;
    border: 1px dashed #cbd5e1;
    border-radius: 4px;
    background: transparent;
    color: #64748b;
    font-size: 11px;
    cursor: pointer;

    &:hover {
      border-color: #3b82f6;
      color: #3b82f6;
      background: #eff6ff;
    }
  }
}

.split-items-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.split-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;

  .item-num {
    width: 20px;
    height: 20px;
    background: #475569;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
  }

  .item-fields {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  .field-select {
    height: 28px;
    padding: 0 24px 0 8px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    background: white;
    font-size: 12px;
    color: #1e293b;
    cursor: pointer;
    outline: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24'%3E%3Cpath fill='%2364748b' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 6px center;

    &:focus {
      border-color: #3b82f6;
    }

    &.bank {
      width: 120px;
    }

    &.funding {
      width: 80px;
    }

    &.rel-acc {
      width: 100px;
    }
  }

  .field-autocomplete {
    flex: 0 0 200px;
    width: 200px;
    max-width: 100%;

    &.v-input {
      font-size: 12px;
    }

    .v-field {
      --v-field-padding-start: 8px;
      --v-field-padding-end: 4px;
      min-height: 28px !important;
      border-radius: 4px;
    }

    .v-field__input {
      min-height: 28px !important;
      padding-top: 0 !important;
      padding-bottom: 0 !important;
      font-size: 12px;
      color: #1e293b;
    }

    .v-field__append-inner,
    .v-field__clearable {
      padding-top: 0;
      align-items: center;
    }

    .v-field__outline {
      --v-field-border-opacity: 1;
      color: #d1d5db;
    }

    .v-field--focused .v-field__outline {
      color: #3b82f6;
    }
  }

  .field-price {
    display: flex;
    align-items: center;
    height: 28px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    width: 110px;

    &:focus-within {
      border-color: #3b82f6;
    }

    input {
      flex: 1;
      width: 70px;
      border: none;
      outline: none;
      padding: 0 6px;
      font-size: 12px;
      font-weight: 600;
      text-align: right;
      color: #1e293b;
    }

    span {
      padding-right: 6px;
      font-size: 11px;
      color: #64748b;
    }
  }

  .field-memo {
    flex: 1;
    min-width: 80px;
    height: 28px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    padding: 0 8px;
    font-size: 12px;
    outline: none;

    &::placeholder { color: #94a3b8; }
    &:focus { border-color: #3b82f6; }
  }

  .item-delete {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 4px;
    color: #9ca3af;
    cursor: pointer;
    flex-shrink: 0;

    &:hover:not(:disabled) {
      background: #fee2e2;
      color: #dc2626;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
}

.split-summary {
  margin-top: 12px;
  padding: 10px 12px;
  background: #f1f5f9;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;

  .summary-row {
    display: flex;
    align-items: center;
    gap: 8px;

    .summary-label {
      font-size: 11px;
      color: #64748b;
    }

    .summary-value {
      font-size: 13px;
      font-weight: 600;
      color: #1e293b;

      &.error { color: #dc2626; }
      &.original { color: #64748b; font-weight: 500; }
    }

    &.difference {
      padding-left: 12px;
      border-left: 1px solid #cbd5e1;

      &.valid .summary-value { color: #16a34a; }
      &.invalid .summary-value { color: #dc2626; }
    }
  }
}

.split-modal-actions {
  padding: 12px 16px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  background: #f8fafc;

  button {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 14px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
  }

  .btn-merge {
    margin-right: auto;
    background: white;
    border: 1px solid #fecaca;
    color: #b91c1c;

    &:hover:not(:disabled) { background: #fef2f2; border-color: #f87171; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .btn-cancel {
    background: white;
    border: 1px solid #d1d5db;
    color: #64748b;

    &:hover { background: #f1f5f9; }
  }

  .btn-delete {
    background: white;
    border: 1px solid #fca5a5;
    color: #dc2626;

    &:hover:not(:disabled) { background: #fee2e2; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .btn-save {
    background: #1e3a5f;
    border: none;
    color: white;

    &:hover:not(:disabled) { background: #2d5a8a; }
    &:disabled { background: #94a3b8; cursor: not-allowed; }
  }
}

.delete-confirm-card {
  .delete-confirm-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
  }
}
</style>
